import { Component, DestroyRef, effect, EventEmitter, inject, OnInit, Output, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { FormControl, FormsModule } from '@angular/forms';
import { distinctUntilChanged, filter, map, Subscription, switchMap, tap } from 'rxjs';
import { PaginatorState } from 'primeng/paginator';
import { Card } from 'primeng/card';
import { SelectModule } from 'primeng/select';
import { TextareaModule } from 'primeng/textarea';
import { CustomFilter, DynamicColumn, TableComponent } from '../../../@shared/components/table-component/table-component';
import { OperationSubmission } from '../../../models/operation-submission';
import { GenericPaginated } from '../../../models/generic-paginated';
import { Params } from '../../../services/utilis/add-params-service';
import { UserRole } from '../../../models/enums';
import { OperationSubmissionService } from '../../../services/operation-submissions/operation-submission-service';
import { EmployeeService } from '../../../services/employees/employee-service';
import { ReviewStreamService } from '../../../services/review-stream/review-stream-service';
import { OperationSubmissionDetails } from '../operation-submission-details/operation-submission-details';
import {
  OperationReviewAction,
  ReviewOperationSubmissionForm
} from '../../../@shared/forms/operation-submissions/review-operation-submission-form/review-operation-submission-form';

import { OPERATION_TAB } from '../../../constants/ride-verification.constants';

// Fleet Manager can view operation submissions but gets a 403 on approve, reject and status
const REVIEW_ROLES = new Set<UserRole>([
  UserRole.CS_AGENT,
  UserRole.CS_SUPERVISOR,
  UserRole.ADMIN,
  UserRole.SUPER_ADMIN,
]);

const STATUS_BADGE_STYLES: Record<string, { background: string; color: string }> = {
  'APPROVED': { background: 'var(--status-available-bg, #dcfce7)', color: 'var(--status-available-text, #16a34a)' },
  'REJECTED': { background: 'var(--status-rejected-bg, #fee2e2)', color: 'var(--status-rejected-text, #dc2626)' },
  'PENDING': { background: 'var(--status-pending-bg, #fef9c3)', color: 'var(--status-pending-text, #854d0e)' },
};

@Component({
  selector: 'app-operation-submission-list',
  imports: [TableComponent, Card, OperationSubmissionDetails, ReviewOperationSubmissionForm, SelectModule, FormsModule, TextareaModule],
  templateUrl: './operation-submission-list.html',
  // Reuses the verification page styles (change status dialog, back button)
  styleUrl: '../ride-verification-list/ride-verification-list.scss'
})
export class OperationSubmissionList implements OnInit {

  // Emitted after any review so the parent can refresh its counts
  @Output() changed = new EventEmitter<void>();

  params = [
    'page',
    'size',
    'search',
    'sortBy',
    'sortDirection',
    'status',
    'createdFrom',
    'createdTo',
  ]

  columns: DynamicColumn<OperationSubmission>[] = [
    { header: 'ID', field: 'id', sortable: true },
    { header: 'Driver', field: 'employeeName', bottomField: 'employeeEmail', isAvatar: true, sortable: true },
    {
      header: 'Plate Number',
      field: 'plateNo',
      sortable: true,
      command: (row) => this.redirectToVehiclesPage(row),
      cellClass: 'text-primary underline cursor-pointer',
    },
    { header: 'Car', field: 'carMaker', mergedName: 'carModel', sortable: false },
    { header: 'Previous Status', field: 'previousStatus', sortable: true, isCapitalize: true },
    { header: 'Submitted At', field: 'createdAt', sortable: true, isDateTime: true },
    {
      header: 'Photo Status',
      field: 'status',
      sortable: true,
      isStatusBadge: true,
      cellClass: 'cursor-pointer',
      command: (row) => this.openChangeStatusDialog(row),
      commandEnabled: () => this.canReview,
      badgeStyleGetter: (value) => STATUS_BADGE_STYLES[value as string] ?? { background: 'transparent', color: 'inherit' },
    },
    {
      field: 'actions',
      header: 'Photos',
      sortable: false,
      actions: [
        {
          isTextButton: true,
          text: 'SHOW',
          command: (row) => this.openDetails(row),
          tooltip: 'View operation photos',
        },
      ],
    },
    { header: 'Reviewed At', field: 'reviewedAt', sortable: true, isDateTime: true },
    { header: 'Verification Comment', field: 'reviewComment', sortable: false },
  ];

  readonly statusOptions = [
    { label: 'Pending', value: 'PENDING' },
    { label: 'Approved', value: 'APPROVED' },
    { label: 'Rejected', value: 'REJECTED' },
  ]

  createdDateFilter: CustomFilter = {
    key: 'created',
    label: 'Submitted Date',
    type: 'date-range',
    options: []
  }

  activeStatus = 'PENDING';

  submissions = signal<OperationSubmission[]>([]);
  totalRecords = signal(0);
  first = 0;
  rows = 20;
  searchControl = new FormControl('');

  selected = signal<OperationSubmission | null>(null);
  selectedIndex = 0;
  detailsOpen = false;
  detailsShowNav = false;
  private pendingOnly = false;

  reviewDialog = false;
  reviewAction: OperationReviewAction = 'approve';

  changeStatusDialog = false
  changeStatusSubmission: OperationSubmission | null = null
  changeStatusValue: string | null = null
  changeStatusReason = ''
  changeStatusLoading = false
  changeStatusError = ''

  private operationSubmissionService = inject(OperationSubmissionService)
  private employeeService = inject(EmployeeService)
  private router = inject(Router)
  private activatedRoute = inject(ActivatedRoute)
  private destroyRef = inject(DestroyRef)
  private reviewStream = inject(ReviewStreamService)
  private reloadSub?: Subscription

  constructor() {
    // Reload the current page when the live pending count changes (first run is the initial load, done in ngOnInit)
    let initialized = false;
    effect(() => {
      this.reviewStream.operationSubmissionCount();
      if (!initialized) {
        initialized = true;
        return;
      }
      untracked(() => this.reload());
    });
    this.destroyRef.onDestroy(() => this.reloadSub?.unsubscribe());
  }

  get canReview(): boolean {
    const role = this.employeeService.employee()?.role;
    return !!role && REVIEW_ROLES.has(role);
  }

  ngOnInit() {
    this.activatedRoute.queryParams.pipe(
      filter(params => params['tab'] === OPERATION_TAB),
      map(params =>
        Object.fromEntries(Object.entries(params).filter(([key]) => this.params.includes(key)))
      ),
      distinctUntilChanged((previous, current) => JSON.stringify(previous) === JSON.stringify(current)),
      tap(queryParams => {
        this.searchControl.setValue(queryParams['search'], { emitEvent: false });
        this.rows = queryParams['size'] ? +queryParams['size'] : 20;
        this.activeStatus = queryParams['status'] ?? 'PENDING';
        this.pendingOnly = this.activeStatus === 'PENDING';
      }),
      switchMap(queryParams => this.fetch(queryParams)),
      takeUntilDestroyed(this.destroyRef),
    ).subscribe(res => this.onListLoaded(res));
  }

  private fetch(queryParams: Params) {
    return this.operationSubmissionService.getOperationSubmissions({
      ...queryParams,
      status: queryParams['status'] ?? 'PENDING',
      sortBy: queryParams['sortBy'] ?? 'createdAt',
      sortDirection: queryParams['sortDirection'] ?? 'DESC',
    });
  }

  private reload() {
    const queryParams = Object.fromEntries(
      Object.entries(this.activatedRoute.snapshot.queryParams).filter(([key]) => this.params.includes(key))
    );
    this.reloadSub?.unsubscribe();
    this.reloadSub = this.fetch(queryParams).subscribe(res => this.onListLoaded(res));
  }

  private onListLoaded(res: GenericPaginated<OperationSubmission>) {
    this.submissions.set(res.data.content);
    this.totalRecords.set(res.data.totalElements);
    this.first = res.data.currentPage * res.data.pageSize;

    const current = this.selected();
    if (this.pendingOnly && res.data.content.length > 0) {
      // Same as the ride Pending tab: open the first pending submission for review,
      // but keep the one under review if a live refresh still lists it
      const stillListed = current && res.data.content.find(s => s.id === current.id);
      this.openDetails(stillListed || res.data.content[0], true);
    } else if (this.detailsShowNav) {
      // Pending queue is empty, fall back to the table
      this.closeDetails();
    } else if (this.detailsOpen && current) {
      // Refresh the open submission so its new status and comment show
      this.openDetails(current);
    }
  }

  setStatus(status: string) {
    if (status === this.activeStatus) return;
    this.activeStatus = status;
    this.closeDetails();
    this.router.navigate([], {
      queryParams: { status, page: 0 },
      queryParamsHandling: 'merge',
    });
  }

  onPageChange = (event: PaginatorState) => {
    this.router.navigate([], {
      queryParams: { page: event.page, size: event.rows },
      queryParamsHandling: 'merge'
    });
  }

  openDetails = (row: OperationSubmission, showNav = false) => {
    this.selectedIndex = this.submissions().findIndex(s => s.id === row.id);
    this.operationSubmissionService.getOperationSubmissionById(row.id).subscribe({
      next: (res) => {
        this.selected.set(res.data);
        this.detailsShowNav = showNav;
        this.detailsOpen = true;
      }
    });
  }

  closeDetails() {
    this.detailsOpen = false;
    this.detailsShowNav = false;
    this.selected.set(null);
  }

  onPrev = () => {
    const idx = this.selectedIndex - 1;
    if (idx >= 0) this.openDetails(this.submissions()[idx], this.detailsShowNav);
  }

  onNext = () => {
    const idx = this.selectedIndex + 1;
    if (idx < this.submissions().length) this.openDetails(this.submissions()[idx], this.detailsShowNav);
  }

  openReview(action: OperationReviewAction) {
    this.reviewAction = action;
    this.reviewDialog = true;
  }

  onReviewed = () => {
    this.reviewDialog = false;
    this.reload();
    this.changed.emit();
  }

  redirectToVehiclesPage = (row: OperationSubmission) => {
    this.router.navigate(['/vehicles'], {
      queryParams: { search: row.plateNo },
      queryParamsHandling: ''
    });
  }

  openChangeStatusDialog = (row: OperationSubmission) => {
    if (!this.canReview) return
    this.changeStatusSubmission = row
    this.changeStatusValue = row.status ?? null
    this.changeStatusReason = row.reviewComment ?? ''
    this.changeStatusError = ''
    this.changeStatusLoading = false
    this.changeStatusDialog = true
  }

  submitChangeStatus = () => {
    if (!this.changeStatusSubmission || !this.changeStatusValue) return
    this.changeStatusLoading = true
    this.changeStatusError = ''
    const payload: { status: string; comment?: string } = { status: this.changeStatusValue }
    if (this.changeStatusReason.trim()) {
      payload.comment = this.changeStatusReason.trim()
    }
    this.operationSubmissionService.updateStatus(this.changeStatusSubmission.id, payload).subscribe({
      next: () => {
        this.changeStatusLoading = false
        this.changeStatusDialog = false
        this.reload()
        this.changed.emit()
      },
      error: () => {
        this.changeStatusLoading = false
        this.changeStatusError = 'Failed to update status. Please try again.'
      }
    })
  }
}
